import React, { FocusEvent, ChangeEvent, KeyboardEvent } from 'react';
import cx from 'classnames';

import AlertError from '../Icons/AlertError';

import styles from './BaseInput.module.scss';

export interface InputProps {
  autoCapitalize?: string;
  autoComplete?: string;
  autoCorrect?: string;
  autoFocus?: boolean;
  children?: React.ReactNode;
  className?: Parameters<typeof cx>[0];
  codeType?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  focused?: boolean;
  iconButtons?: React.ReactElement[];
  iconButtonWrapperClassName?: Parameters<typeof cx>[0];
  id: string;
  inputClassName?: Parameters<typeof cx>[0];
  inputMode?:
    | 'decimal'
    | 'email'
    | 'none'
    | 'numeric'
    | 'search'
    | 'tel'
    | 'text'
    | 'url';
  isDarkMode?: boolean;
  label: string;
  labelClassName?: Parameters<typeof cx>[0];
  large?: boolean;
  maxLength?: number;
  minLength?: number;
  name?: string;
  note?: React.ReactNode;
  noteIcon?: React.ReactElement;
  noteId?: string;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
  pattern?: string;
  placeholder?: string;
  prefix?: (callbacks: {
    onFocus: () => void;
    onBlur: () => void;
  }) => React.ReactElement;
  prefixClassName?: Parameters<typeof cx>[0];
  readOnly?: boolean;
  ref?: any;
  required?: boolean;
  /**
   * Specific the height of the input.
   *
   * @default 48
   */
  size?: 40 | 48 | 56;
  value?: string;
}

export interface Props extends InputProps {
  type: string;
}

export const BaseInput: React.ForwardRefExoticComponent<Props> = React.forwardRef(
  (
    {
      autoCapitalize,
      autoComplete,
      autoCorrect,
      autoFocus,
      children,
      className,
      codeType,
      disabled,
      errorMessage,
      focused,
      iconButtons,
      iconButtonWrapperClassName,
      id,
      inputClassName,
      inputMode,
      isDarkMode,
      label,
      labelClassName,
      large,
      maxLength,
      minLength,
      name,
      note,
      noteIcon,
      noteId,
      onBlur,
      onChange,
      onFocus,
      onKeyDown,
      onKeyPress,
      pattern,
      placeholder,
      prefix,
      prefixClassName,
      readOnly,
      required,
      type,
      size = 48,
      value,
    },
    ref: any,
  ) => {
    const floatLabel = Boolean(value || focused || placeholder);
    const invalid = Boolean(errorMessage);
    const alertId = `${id}-alert`;

    return (
      <div
        className={cx(
          styles.container,
          isDarkMode && styles.containerDark,
          disabled && styles.disabled,
          large && styles.largeContainer,
          prefix && styles.containerPrefixed,
          (errorMessage || note) && styles.hasNoteContainer,
          className,
        )}
      >
        {prefix && (
          <span className={cx(styles.prefix, prefixClassName)}>
            {prefix({ onFocus: () => null, onBlur: () => null })}
          </span>
        )}
        <input
          className={cx(
            styles.input,
            inputClassName,
            value !== null && value !== '' && styles.hasValue,
            isDarkMode && styles.inputDark,
            large && styles.largeInput,
            invalid && styles.hasError,
            codeType && styles.codeType,
            styles[`size${size}`],
          )}
          {...{
            autoCapitalize,
            autoComplete,
            autoCorrect,
            autoFocus,
            disabled,
            inputMode,
            maxLength,
            minLength,
            name,
            onBlur,
            onChange,
            onFocus,
            onKeyDown,
            onKeyPress,
            pattern,
            readOnly,
            ref,
            required,
            type,
            value,
          }}
          aria-describedby={invalid || note || noteIcon ? alertId : undefined}
          aria-invalid={invalid ? 'true' : undefined}
          id={id}
          placeholder={placeholder}
          spellCheck='false'
        />
        {iconButtons && (
          <div
            className={cx(iconButtonWrapperClassName, styles.iconButtonWrapper)}
          >
            {iconButtons.map((iconButton, i) =>
              iconButton
                ? React.cloneElement(iconButton, {
                    className: cx(
                      iconButton.props.className,
                      styles.iconButton,
                    ),
                    key: i,
                  })
                : null,
            )}
          </div>
        )}
        {invalid ? (
          <InputAlert id={alertId} isDarkMode={isDarkMode}>
            {errorMessage}
          </InputAlert>
        ) : (
          (note || noteIcon) && (
            <InputNote id={alertId} noteIcon={noteIcon} noteId={noteId}>
              {note}
            </InputNote>
          )
        )}
        <label
          className={cx(
            styles.label,
            isDarkMode && styles.labelDark,
            !floatLabel && large && styles.largeLabel,
            floatLabel && [
              styles.floatLabel,
              isDarkMode && styles.floatLabelDark,
            ],
            labelClassName,
          )}
          htmlFor={id}
        >
          {label}
        </label>
        {children}
      </div>
    );
  },
);

export const InputAlert: React.FC<Pick<Props, 'id' | 'isDarkMode'>> = ({
  children,
  id,
  isDarkMode,
}) => (
  <span className={cx(styles.noteContainer)} role='alert' id={id}>
    <AlertError
      accessibilityLabel='Error'
      className={cx(styles.noteIcon, styles.errorIcon)}
    />
    <span className={cx(styles.note, isDarkMode && styles.noteDark)}>
      {children}
    </span>
  </span>
);

export const InputNote: React.FC<Pick<
  Props,
  'id' | 'noteIcon' | 'noteId' | 'isDarkMode'
>> = ({ children, id, noteIcon, noteId, isDarkMode }) => (
  <span id={noteId || id} className={styles.noteContainer}>
    {noteIcon &&
      React.cloneElement(noteIcon, {
        className: cx(styles.noteIcon, noteIcon.props.className),
      })}
    <span className={cx(styles.note, isDarkMode && styles.noteDark)}>
      {children}
    </span>
  </span>
);

BaseInput.displayName = 'BaseInput';
export default BaseInput;
export { styles };
